/**
 * FilterBarReposive
 * 
 * @Author: Focci
 * @Date: 2023-12-04 14:16:37
 * @Last Modified by: Focci
 * @Last Modified time: 2023-12-04 14:16:37
 */

'use client'

import PropTypes from 'prop-types'
import SubcribeTrigger from '@comp/page/search/common/subcribe/SubcribeTrigger'
import SVGFilter from '@img/filter.svg'
import SVGAddSubcribe from '@img/add-subcribe.svg'
import SVGAddedSubcribe from '@img/added-subcribe.svg'
import SVGSort from '@img/sort.svg'
import clsx from 'clsx'
import useProgressRouter from '@hook/useProgressRouter'
import { useTranslations } from 'next-intl'
import { ButtonBase } from '@comp/material'
import { SortDropdownMenu } from '@comp/page/search/common/dropdown'
import { getSortOptions } from '@comp/page/search/common/dropdown/lib'
import { map } from 'lodash-es'
import { useCallback, useMemo } from 'react'
import { getFilterCount } from './lib'

function FilterButtonItem({
  icon,
  badge,
  label = '',
  ...props
}) {
  return (
    <ButtonBase className="rounded-full w-full" {...props}>
      <div 
        className={clsx([
          'flex items-center justify-center h-8 rounded-full',
          'gap-x-1 px-2 w-full'
        ])}
      >
        <span>{icon}</span>
        <span className="whitespace-nowrap line-clamp-1">{label}</span>
        {!!badge && (
          <span className="bg-primary rounded-[200px] text-white text-f.6 px-1">
            {badge}
          </span>
        )}
      </div>
    </ButtonBase>
  )
}

function FilterButtonSubcribe({
  onClick,
  isSubcribed
}) {
  const tt = useTranslations('track')
  const tl = useTranslations('live')

  return (
    <FilterButtonItem 
      label={isSubcribed ? tl('subscribed') : tt('subcribe')}
      disabled={isSubcribed}
      onClick={onClick}
      className="text-white bg-primary rounded-full"
      bordered={false}
      icon={
        isSubcribed 
          ? <SVGAddedSubcribe width=".8rem" />
          : <SVGAddSubcribe width=".8rem" />
      }
    />
  )
}

function FilterButtonSort({
  searchParam = {}
}) {
  const ts = useTranslations('search')
  const options = getSortOptions(searchParam.type, ts)
  const order = searchParam.order || ''
  const index = map(options, 'value').indexOf(order)

  return (
    <FilterButtonItem 
      label={options[index]?.label}
      icon={<SVGSort width=".8rem" />}
    />
  )
}

export default function FilterBarReposive({
  searchParam = {},
  type,
  isMapListing,
  onClick = () => {},
}) {
  const ts = useTranslations('search')
  const { routerPush } = useProgressRouter()

  const count = useMemo(() => getFilterCount(searchParam), [searchParam])
  
  const handleChange = useCallback(({ item, url }) => {
    if(searchParam.sort === item.value) return
    routerPush(url)
  }, [routerPush, searchParam.sort])

  return (
    <div className="grid grid-cols-3 gap-x-2 w-full">
      <div className="flex col-span-2 border rounded-full">
        <div style={{ width: 'calc(50% - 1px)' }}>
          <FilterButtonItem 
            label={ts('filter')}
            icon={<SVGFilter width=".7rem" />}
            onClick={onClick}
            badge={count}
          />
        </div>
        <span className="border-l" />
        <div style={{ width: 'calc(50% - 1px)' }} className="flex justify-center">
          <SortDropdownMenu 
            isMapListing={isMapListing}
            type={type}
            searchParam={searchParam}
            onChange={handleChange}
            trigger={<FilterButtonSort searchParam={searchParam} />}
          />
        </div>
      </div>
      <SubcribeTrigger
        type={type}
        searchParam={searchParam}
        Trigger={FilterButtonSubcribe}
      />
    </div>
  )
}

FilterBarReposive.propTypes = {
  type: PropTypes.string.isRequired,
  isMapListing: PropTypes.bool,
  onClick: PropTypes.func,
}
